import type {
  NodeKey,
  NodeSource,
  NodeUnit,
  SlotInputNode,
  SlotValuationInputsResponse,
} from 'types/packages-api/slot-valuation-inputs';
import {
  buildSections,
  buildSectionsWithBlocks,
  type ZoneSection,
} from './tearsheet-sections';
import {
  blockCaptionsForSection,
  disclosedSeriesKeys,
  EXPENSE_CLASS_LABELS,
  notUsedRowsForSection,
  renderedRowNodes,
} from './source-blocks';
import {
  type RowGateContext,
  rowGateContextFor,
  rowNotesForSection,
} from './row-gate-utils';
import { overrideBadge, overrideBadgeText } from './override-badge';
import { nodeKeyForMilestone } from './editability';
import {
  COMMISSION_GROUPS,
  commissionGroupHeading,
  dateSourceLabel,
  DOMAIN_CONTEXT_NOUN,
  formatNodeExport,
  formatNodeValue,
  formatOutputValue,
  MILESTONE_LABELS,
  MILESTONE_ORDER,
  outputLabel,
  outputRows,
  scheduleOperands,
  showsScheduleFormula,
  slotCategoryOf,
  UNIT_HEADER_SUFFIX,
  unitDisplayScale,
} from './utils';

/**
 * ── THE EXCEL EXPORT'S CONTENT, BUILT WHERE THE PAGE'S CONTENT IS BUILT ──────────────────
 *
 * UAT item 11. The workbook has to hold exactly what the tearsheet holds, and the tearsheet's
 * rows are not in the payload — they are the product of `buildSections`, `renderedRowNodes`
 * (which drops a disclosed monthly node and a rate scoped to another reserve category), and
 * `classifyExpenses` (which walks the registry's edges AND regex-scans formula text).
 *
 * So this module does not re-derive them. It calls THE SAME FUNCTIONS the table calls, in the
 * same order, and turns each rendered row into one flat record. A Ruby exporter re-deriving
 * this would be a second implementation of a subtle spec in a second language; the two would
 * drift, and a workbook that quietly disagrees with the page it was taken from is the exact
 * class of defect this epic exists to close.
 *
 * The server writes the file (libxlsxwriter can emit frozen panes; SheetJS cannot) but
 * decides nothing about its content. See SlotTearsheetExporter.
 *
 * @module
 */

/**
 * Decimal cap the export passes to {@link formatNodeValue} for scaled units (fraction /
 * percent). The page rounds those to 4 decimals, which is fine on screen; a spreadsheet is
 * where a reader goes FOR precision, so a rounded fraction written into a cell can propagate
 * into their own maths (INS-12206 AC). 8 matches the default numeric branch's own cap, so the
 * export never truncates a fraction the plain-number path would have kept.
 */
export const EXPORT_SCALED_MAX_PRECISION = 8;

/** One flat row of sheet 1. A null field is a BLANK CELL, never a zero and never "null". */
export interface TearsheetExportRow {
  /**
   * `'blockHeader'` marks a per-block source header (INS-12210): the scenario / named
   * component the block came from, whose `link` the writer renders as a REAL Excel hyperlink
   * and whose position it uses to open a collapsible group. `'blockNote'` marks a block's
   * caption written as a row of its own (INS-13307), for a block with no header to carry it.
   * Both are captions on the page, not table rows. Absent on an ordinary data row.
   */
  kind?: 'blockHeader' | 'blockNote';
  block: string;
  row: string;
  value: string | null;
  unit: string | null;
  source: string | null;
  formula: string | null;
  operands: string | null;
  link: string | null;
  /** The tab holding this row's monthly series, or null when it has none. */
  monthlyDetail: string | null;
}

/** One monthly-series tab. Months down the rows, one column per stream. */
export interface TearsheetExportSeries {
  name: string;
  columns: string[];
  /** `[monthNumber, isoDate, ...values]`, one entry per month. */
  rows: Array<Array<string | number | null>>;
}

export interface TearsheetExportBody {
  rows: TearsheetExportRow[];
  series: TearsheetExportSeries[];
}

/** A node's served definition fields, as the table view already assembles them. */
export interface ExportNodeMeta {
  unit?: NodeUnit;
  source?: NodeSource;
  category?: string;
  label?: string;
  /** The `dates` entry this node schedules, on the milestone nodes only. */
  milestone?: string;
  /** Whether an override on this node moves the value its own row shows. */
  overrideReachesRow?: boolean;
  /** The registry's reader note, where one is served (INS-13307). */
  note?: string;
}

export type ExportMetaByNode = Record<string, ExportNodeMeta | undefined>;

/** The Calculated result's own block, named as the demo names it. */
export const RESULTS_BLOCK_LABEL = 'Results';

/**
 * The five source kinds the payload actually declares, in the payload's own vocabulary.
 *
 * NOT the demo workbook's Source strings. The demo is a LAYOUT proposal and says so in its
 * own third row; its "Matched by Unit" / "Set by you" labels have no counterpart in
 * `NodeSource`, which is `'declared_only' | [NodeSourceKind, ...]` with four kinds. Inventing
 * a mapping onto words the data cannot support would put a claim in the file that the page
 * never makes. A date row is different — it HAS a served provenance, and uses dateSourceLabel,
 * which is where the demo's "On file" and "Scheduled" genuinely come from.
 */
const SOURCE_KIND_LABELS: Record<string, string> = {
  parquet: 'Parquet',
  blob: 'Blob',
  column: 'Column',
  json_column: 'JSON column',
};

/**
 * A node's source, in words, or null while the model map has not landed.
 *
 * @param {NodeSource} [source] - The served source.
 * @return {(string|null)} The label, or null when nothing can be claimed.
 */
export function sourceLabel(source: NodeSource | undefined): string | null {
  if (source === 'declared_only') {
    return 'Declared only';
  }
  if (Array.isArray(source) && typeof source[0] === 'string') {
    return SOURCE_KIND_LABELS[source[0]] ?? null;
  }
  return null;
}

/**
 * The Value cell for one node, mirroring NodeValue's branches in text.
 *
 * ── AN ABSENT VALUE RETURNS null, NOT A DASH ─────────────────────────────────────────────
 *
 * The page draws `EMPTY` (a dash) so the reader can see the row exists and has nothing. A
 * spreadsheet says the same thing with an empty cell, and says it BETTER: a literal "—" is a
 * string that breaks sorting, filtering and any formula pointed at the column. The dash is a
 * rendering of absence, not the absence itself.
 *
 * A node whose formula IS its value (`applied: false` with a formula) returns null here and
 * carries its formula in the Formula column, which is where a flat table can put it.
 *
 * @param {SlotInputNode} node - The node the row was built from.
 * @param {NodeUnit} [unit] - Its served unit.
 * @return {(string|null)} The rendered value, or null for a blank cell.
 */
export function exportValue(
  node: SlotInputNode,
  unit: NodeUnit | undefined
): string | null {
  if (!node.applied || node.values.length === 0) {
    return null;
  }
  if (node.seriesKey) {
    // A monthly node that reaches here has NO disclosure column anywhere on the page —
    // renderedRowNodes already removed the ones that do. Its handful of preview values are
    // the only values a reader gets, so they are what the cell carries.
    return node.values
      .map(
        (entry) =>
          `${entry.label}: ${formatNodeValue(
            entry.value,
            unit,
            EXPORT_SCALED_MAX_PRECISION
          )}`
      )
      .join(', ');
  }
  // A DCA forecast's name, as the page shows it: plain text, so a text cell, never a number.
  if (node.valueLabel) {
    return node.valueLabel;
  }
  return formatNodeExport(
    node.key,
    node.values[0].value,
    unit,
    EXPORT_SCALED_MAX_PRECISION
  );
}

/** A per-block named source: the scenario / component the block came from, and its link. */
export interface BlockSourceHeader {
  /** How to name the source — "Scenario", "Price deck", "Type curve". */
  noun: string;
  /** The source's display name, or null when the run recorded only an id. */
  name: string | null;
  /** A link to the source, or null when there is nowhere to go. */
  href: string | null;
  /** The id to link on when there is no name, so a link still has text. */
  id: string | number | null;
}

/**
 * ── THE NAMED SOURCE A BLOCK CAME FROM, READ WHERE THE PAGE READS IT (INS-12210) ──────────
 *
 * Andrew, on the Excel export: open each block with the scenario / named component it came
 * from, linked, so a reader can jump to where the data lives. The page ALREADY resolves this
 * for its own on-screen block header — a domain's scenario name + href (ZoneSectionView) and
 * the type curve's name + href (TypeCurveHeader) — so this reads THE SAME fields rather than
 * re-deriving them, and the workbook header can never disagree with the page's.
 *
 * Returns null for a block with no named source (Volume Source, Slot inputs, Results); the AC
 * says those get no header row rather than an empty one.
 *
 * Development pace flows through the domain branch too, so it lights up on its own once
 * INS-12204 puts its scenario name + href on the payload — no change here.
 *
 * @param {ZoneSection} section - The block, as buildSections produced it.
 * @param {SlotInputNode[]} allNodes - Every node, for the type curve's own node.
 * @return {(BlockSourceHeader|null)} The named source, or null when the block has none.
 */
export function blockSourceHeader(
  section: ZoneSection,
  allNodes: SlotInputNode[]
): BlockSourceHeader | null {
  // A domain block: the scenario / deck that supplied its values. THE SAME condition the page
  // uses to show its context line — a domain, and either a name or an href (never the noun
  // alone, which is how a dead link ships).
  if (section.domain && (section.scenario || section.scenarioHref)) {
    return {
      noun: DOMAIN_CONTEXT_NOUN[section.domain],
      name: section.scenario,
      href: section.scenarioHref,
      id: section.scenarioId,
    };
  }
  // The merged volume-source block: name + href from the slot_volume_source node, exactly
  // as TypeCurveHeader reads them, and only when the volumes actually came from a type
  // curve. The zone used to be 'type_curve'; it is 'volumes' since the merge, and the
  // volumeSource check is what still narrows it to the curve case.
  if (section.zone === 'volumes' && section.volumeSource === 'type_curve') {
    const node = allNodes.find(
      (candidate) => candidate.key === 'slot_volume_source'
    );
    const name = node?.values?.[0]?.value;
    if (typeof name === 'string' && name) {
      return {
        noun: 'Type curve',
        name,
        href: node?.valueHref ?? null,
        id: null,
      };
    }
  }
  return null;
}

/**
 * One block's header row, or null when the block has no named source.
 *
 * The whole cell is the link, so the writer makes ONE hyperlink of "Scenario: MGM Grand". A
 * run that recorded an id and no name still links, on the id.
 *
 * @param {ZoneSection} section - The block.
 * @param {SlotInputNode[]} allNodes - Every node.
 * @return {(TearsheetExportRow|null)} The header row, or null.
 */
function headerRow(
  section: ZoneSection,
  allNodes: SlotInputNode[]
): TearsheetExportRow | null {
  const header = blockSourceHeader(section, allNodes);
  if (!header) {
    return null;
  }
  const label = header.name ?? (header.id !== null ? String(header.id) : null);
  return {
    kind: 'blockHeader',
    block: section.label,
    row: label ? `${header.noun}: ${label}` : header.noun,
    value: null,
    unit: null,
    source: null,
    formula: null,
    operands: null,
    link: header.href,
    monthlyDetail: null,
  };
}

/**
 * Builds every row of sheet 1, in the order the page renders them.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {ExportMetaByNode} meta - Served node definitions, as the table view holds them.
 * @return {TearsheetExportRow[]} The flat rows.
 */
export function buildExportRows(
  response: SlotValuationInputsResponse,
  meta: ExportMetaByNode
): TearsheetExportRow[] {
  // `meta` IS PASSED NOW, and it has to be: buildSections threads it to the merged block's
  // caption as the node-source lookup, and without it every workbook would report
  // `unknown_source` and lose the "Type curve: <name>" block header. It still renders no
  // edit affordance — that is gated on `overridable`, not on the map being present.
  const { sections, blocks } = buildSectionsWithBlocks(response, meta);
  const disclosed = disclosedSeriesKeys(blocks, response);
  const slotCategory = slotCategoryOf(response.slotInputs ?? []);
  const allNodes = response.slotInputs ?? [];
  const seriesTabOf = seriesTabByColumn(sections);
  // The page's own context, from the same builder (tearsheet-zone-section.tsx), so both gates
  // below answer identically on the page and in the file.
  const gateContext: RowGateContext = rowGateContextFor(response, meta);
  const rows: TearsheetExportRow[] = [];

  for (const section of sections) {
    // THE PAGE'S OWN GATE, not a second copy of it: the page's rows and the workbook's are
    // the same rows by construction, so a marker the page shows and the file omits (or the
    // reverse) would be the file asserting a different fact about the same value.
    //
    // THE SENTENCE TRAVELS TOO (INS-13307). The file used to write one generic note for every
    // marker, so a row the page explained as "Not used: water from the DCA forecast" said only
    // "not used by this run" in the workbook. The Source cell now carries the page's own
    // sentence, whichever gate marked the row.
    const markedNotUsed = notUsedRowsForSection(
      section,
      allNodes,
      (key: NodeKey) => meta[key]?.source,
      gateContext
    );
    // Per block, like the page's: a row this block marks not used carries no note.
    const rowNotes = rowNotesForSection(allNodes, gateContext, markedNotUsed);
    const rowsFor = (nodes: SlotInputNode[]) =>
      renderedRowNodes(
        nodes,
        disclosed,
        (key: NodeKey) => meta[key]?.category,
        slotCategory,
        // The workbook's rows are the page's rows BY CONSTRUCTION, not by a second copy of
        // the filter agreeing with it — so the absent-stream rule reaches the export too.
        (key: NodeKey) => meta[key]?.source
      );

    // INS-12210: the block opens with its named source (scenario / type curve, linked), above
    // even the milestone rows — the first row of the block, so the writer groups the rest
    // under it. Absent when the block has no named source.
    //
    // INS-13307: the page's block captions (the volume source caption, the type-curve note, the
    // Volume Adjustment Ratio note, the leasehold note) ride in that row's Source cell, in the
    // page's order, read from the same functions, rendered rows and columns; a block with a
    // note and no header gets a row of its own.
    const header = headerRow(section, allNodes);
    const sectionNote =
      blockCaptionsForSection(section, rowsFor(section.nodes), allNodes, {
        response,
        sourceFor: (key: NodeKey) => meta[key]?.source,
        unitFor: (key: NodeKey) => meta[key]?.unit,
      }).join(' ') || null;
    if (header) {
      rows.push(sectionNote ? { ...header, source: sectionNote } : header);
    } else if (sectionNote) {
      rows.push(blockNoteRow(section.label, sectionNote));
    }

    // The four milestones lead the Development Pace block, exactly as DatesTable renders
    // above that block's parameter table.
    if (section.zone === 'development_pace') {
      rows.push(...dateRows(section, response, allNodes, meta));
    }

    // An expenses block renders its capex/opex groups instead of one flat list, and the
    // group's label goes in the Block column so a collapsed group still says what it is.
    if (section.groups.length > 0) {
      for (const group of section.groups) {
        for (const node of rowsFor(group.nodes)) {
          rows.push(
            nodeRow(
              `${section.label} — ${EXPENSE_CLASS_LABELS[group.expenseClass]}`,
              node,
              meta,
              seriesTabOf,
              markedNotUsed,
              rowNotes
            )
          );
        }
      }
    } else {
      for (const node of rowsFor(section.nodes)) {
        rows.push(
          nodeRow(
            section.label,
            node,
            meta,
            seriesTabOf,
            markedNotUsed,
            rowNotes
          )
        );
      }
    }
  }

  rows.push(...resultRows(response, meta));
  return rows;
}

/**
 * The Calculated result section, which is a block on the page and a block in the file.
 *
 * ── THE THIRD STATE SURVIVES INTO THE WORKBOOK ───────────────────────────────────────────
 *
 * `outputRows` reports three states and they are three different facts. `no_value` is this
 * well having none; `not_computed` is the run never having produced the column at all. The
 * page says so in words beside a dash. A spreadsheet cannot caption a cell, so the
 * distinction goes in the Source column — the only honest place for it — and BOTH leave the
 * Value cell empty. Collapsing them to one blank would assert the two are the same, which is
 * the exact conflation this epic keeps closing.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {ExportMetaByNode} meta - Served node definitions.
 * @return {TearsheetExportRow[]} The Calculated result's rows.
 */
function resultRows(
  response: SlotValuationInputsResponse,
  meta: ExportMetaByNode
): TearsheetExportRow[] {
  const values = response.outputs?.values;
  // ── UAT 6: THE COMMISSION GROUP RIDES IN THE BLOCK COLUMN ────────────────────────────
  //
  // The page heads each NPV pair with its commission rate; the workbook is flat, so the
  // heading goes where a flat table can carry it — the Block column, exactly as the expenses
  // groups do. Without it the six NPVs arrive in the file as the same undifferentiated pairs
  // Andrew objected to on screen, and the export would disagree with the page it came from.
  const groupOf = new Map<string, string>();
  COMMISSION_GROUPS.forEach((group) => {
    const heading = commissionGroupHeading(
      group.label,
      response.header?.commissionRates?.[group.kind]
    );
    group.keys.forEach((key) =>
      groupOf.set(key, `${RESULTS_BLOCK_LABEL} — ${heading}`)
    );
  });
  // The same unvalued rule as the page, so the file does not write "Not computed by this
  // run" on every row of a slot the backend chose not to value.
  return outputRows(values, Boolean(response.outputs?.unvaluedReason)).map(
    ({ key, state }) => ({
      block: groupOf.get(key) ?? RESULTS_BLOCK_LABEL,
      row: outputLabel(key, meta[key]?.label),
      // formatOutputValue, NOT formatNodeValue — the two disagree by up to 8 decimals on the
      // same NPV, and the page settled that an output row uses the former.
      //
      // EXPORT_SCALED_MAX_PRECISION so the workbook keeps a fraction/percent output (irr) at
      // full precision, matching the input rows two blocks up (exportValue). Without it, a raw
      // xirr of 0.18523456 landed as "18.5235%" here while an NRI input rounded to 0.390625% —
      // the same file rounding one and not the other. The page path passes
      // nothing and keeps its 4-dp default.
      value:
        state === 'value'
          ? formatOutputValue(
              values?.[key],
              meta[key]?.unit,
              EXPORT_SCALED_MAX_PRECISION
            )
          : null,
      unit: meta[key]?.unit
        ? (UNIT_HEADER_SUFFIX[meta[key]!.unit!] ?? null)
        : null,
      source:
        state === 'not_computed'
          ? 'Not computed by this run'
          : state === 'no_value'
            ? 'No value for this well'
            : 'Computed',
      formula: null,
      operands: null,
      link: null,
      monthlyDetail: null,
    })
  );
}

/**
 * A block's note on a row of its own, for a block with no named-source header to carry it.
 *
 * @param {string} block - The block's label.
 * @param {string} note - The note, written to the Source cell.
 * @return {TearsheetExportRow} The row.
 */
function blockNoteRow(block: string, note: string): TearsheetExportRow {
  return {
    kind: 'blockNote',
    block,
    row: 'Note',
    value: null,
    unit: null,
    source: note,
    formula: null,
    operands: null,
    link: null,
    monthlyDetail: null,
  };
}

/**
 * The four schedule milestones, with the per-date formula and its operands.
 *
 * @param {ZoneSection} section - The Development Pace section, for its label.
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {SlotInputNode[]} allNodes - Every node, for resolving operand values.
 * @param {ExportMetaByNode} meta - Served node definitions.
 * @return {TearsheetExportRow[]} One row per milestone, or none when there are no dates.
 */
function dateRows(
  section: ZoneSection,
  response: SlotValuationInputsResponse,
  allNodes: SlotInputNode[],
  meta: ExportMetaByNode
): TearsheetExportRow[] {
  const dates = response.dates;
  if (!dates) {
    return [];
  }
  return MILESTONE_ORDER.map((milestone) => {
    const merged = dates[milestone];
    // The badge the Milestones row shows, read against the row's merged date exactly as
    // MilestoneOverrideMarker reads it.
    const nodeKey = nodeKeyForMilestone(milestone, meta);
    const node = nodeKey
      ? allNodes.find((candidate) => candidate.key === nodeKey)
      : undefined;
    const badge = node
      ? overrideBadge(node, meta[node.key]?.unit, {
          merged,
          meta: meta[node.key],
        })
      : null;
    // Blank cell (null), never the literal em-dash, for a milestone with no date (INS-11969
    // review). formatNodeValue(null, 'date') returns EMPTY ('—'), which is TRUTHY, so the old
    // `|| null` never fired and a dateless milestone was written as the string '—' — the very
    // thing this module argues against (a literal '—' breaks sorting/filtering/formulas).
    // Coalesce to null and format only a real value.
    const milestoneDate = merged?.value ?? null;
    const showFormula =
      showsScheduleFormula(merged?.source) && Boolean(merged?.formula);
    const operands = showFormula
      ? scheduleOperands(merged?.formulaInputs, allNodes, meta)
      : [];
    return {
      block: section.label,
      row: MILESTONE_LABELS[milestone],
      value:
        milestoneDate === null ? null : formatNodeValue(milestoneDate, 'date'),
      unit: 'date',
      source: sourceCell(
        merged?.source ? dateSourceLabel(merged.source) : null,
        [badge ? overrideBadgeText(badge.applied, badge.pendingValue) : null]
      ),
      formula: showFormula ? (merged?.formula ?? null) : null,
      // One line, joined the way the row joins them. An UNRESOLVED operand keeps its label
      // and shows nothing after the `=`, because absent and zero are different facts and a
      // zero-month lag is ordinary.
      operands: operands.length
        ? operands
            .map((operand) => `${operand.label} = ${operand.text}`)
            .join(' · ')
        : null,
      link: null,
      monthlyDetail: null,
    };
  });
}

/**
 * One node's row.
 *
 * @param {string} block - The Block column's value, including any expense group.
 * @param {SlotInputNode} node - The node this row was built from.
 * @param {ExportMetaByNode} meta - Served node definitions.
 * @param {Map} seriesTabOf - Series column key to the tab that holds it.
 * @param {Map} notUsed - Node keys whose value did not reach this run's result, each to the
 *   sentence the page's marker carries.
 * @param {Map} notes - Node keys to the page's row note (INS-13307).
 * @return {TearsheetExportRow} The flat row.
 */
function nodeRow(
  block: string,
  node: SlotInputNode,
  meta: ExportMetaByNode,
  seriesTabOf: Map<string, string>,
  notUsed = new Map<NodeKey, string>(),
  notes = new Map<NodeKey, string>()
): TearsheetExportRow {
  const unit = meta[node.key]?.unit;
  // The page's own badge, untruncated: a cell has room for the whole pending value.
  const badge = overrideBadge(node, unit, { meta: meta[node.key] });
  return {
    block,
    row: node.label,
    value: exportValue(node, unit),
    unit: unit ? (UNIT_HEADER_SUFFIX[unit] ?? null) : null,
    // ── ⚠️ FOLDED INTO Source, NOT A NINTH COLUMN ────────────────────────────────────
    //
    // This column already carries exactly this kind of sentence: the Results block writes
    // "Not computed by this run" into it (see #resultRow). A new column would be one more
    // thing every reader of the workbook has to learn, and eight of nine cells in it would
    // be empty. The VALUE is left alone — the figures stay on screen,
    // and a marked-up number is not a number.
    //
    // The override badge rides the same way: without it a pending override exports the
    // pre-override value with nothing to say a different one is recorded.
    source: sourceCell(sourceLabel(meta[node.key]?.source), [
      badge ? overrideBadgeText(badge.applied, badge.pendingValue) : null,
      notUsed.has(node.key) ? notUsedExportNote(notUsed.get(node.key)) : null,
      notes.get(node.key) ?? null,
    ]),
    formula: node.formula ?? null,
    operands: null,
    // Only where the SERVER resolved one. Never derived from the value's text, which is how
    // a dead link ships.
    link: node.valueHref ?? null,
    monthlyDetail: node.seriesKey
      ? (seriesTabOf.get(node.seriesKey) ?? null)
      : null,
  };
}

/** The workbook's form of the row marker. */
export const NOT_USED_EXPORT_NOTE = 'not used by this run';

/**
 * The workbook's not-used note for one row: the page's own sentence, led by "not used" so a
 * reader filtering the Source column finds every marked row with one search (INS-13307). A
 * sentence that already says so (`Not used: water from the DCA forecast`) is written as is;
 * one that does not (the Apply AFE and area sentences) follows NOT_USED_EXPORT_NOTE.
 *
 * @param {string} [explanation] - The page marker's sentence.
 * @return {string} The Source-cell note.
 */
export function notUsedExportNote(explanation?: string): string {
  if (!explanation) {
    return NOT_USED_EXPORT_NOTE;
  }
  return /^not used\b/i.test(explanation)
    ? explanation
    : `${NOT_USED_EXPORT_NOTE}: ${explanation}`;
}

/**
 * The Source cell: the source in words, then each row note that applies, dash-separated.
 *
 * @param {(string|null)} label - The row's source in words, or null.
 * @param {Array} notes - The row's markers (override, not used); null where one is absent.
 * @return {(string|null)} The cell, or null when there is nothing to say.
 */
function sourceCell(
  label: string | null,
  notes: Array<string | null>
): string | null {
  const parts = [label, ...notes].filter((part): part is string =>
    Boolean(part)
  );
  return parts.length > 0 ? parts.join(' — ') : null;
}

/**
 * Which tab each series column lives on, so a row can name it.
 *
 * @param {ZoneSection[]} sections - The page's sections, in render order.
 * @return {Map} Series column key to its tab name.
 */
function seriesTabByColumn(sections: ZoneSection[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const section of sections) {
    for (const column of section.seriesColumns) {
      out.set(column, section.label);
    }
  }
  return out;
}

/**
 * Builds the monthly-series tabs — ONE PER BLOCK, not one per series.
 *
 * Sam settled that: a tab per series is about 34 of them, a tab per block is about six. A
 * block with no monthly columns gets no tab.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {ExportMetaByNode} meta - Served node definitions, for the column headers' units.
 * @return {TearsheetExportSeries[]} The tabs, in block order.
 */
export function buildExportSeries(
  response: SlotValuationInputsResponse,
  meta: ExportMetaByNode
): TearsheetExportSeries[] {
  const series = response.outputs?.cashflowSeries;
  if (!series?.rows?.length) {
    return [];
  }
  const allNodes = response.slotInputs ?? [];
  const labelFor = (key: string) =>
    meta[key]?.label ?? allNodes.find((node) => node.key === key)?.label ?? key;

  return buildSections(response)
    .filter((section) => section.seriesColumns.length > 0)
    .map((section) => ({
      name: section.label,
      columns: section.seriesColumns.map((column) => {
        const suffix = UNIT_HEADER_SUFFIX[meta[column]?.unit as NodeUnit];
        return suffix ? `${labelFor(column)} (${suffix})` : labelFor(column);
      }),
      rows: series.rows.map((row, index) => [
        index + 1,
        row.date,
        // A null stays null: the cell is left EMPTY rather than written as 0. `series_points`
        // drops nulls per node, so a column can legitimately have no value in a month its
        // neighbours do.
        ...section.seriesColumns.map((column) => {
          const value = row[column];
          if (value === null || value === undefined) {
            return null;
          }
          // The header says BBL/MMCF for a per-Mcf unit (INS-13307), so the number moves with it.
          // Rounded to 12 significant digits: the product carries float noise into the cell
          // (0.1234 × 1000 is 123.39999999999999), and no stored WGR has that many.
          const scale = unitDisplayScale(meta[column]?.unit);
          return typeof value === 'number' && scale !== 1
            ? Number((value * scale).toPrecision(12))
            : value;
        }),
      ]),
    }));
}

/**
 * The whole request body for POST /queries/packages/slot_valuation_inputs/exports.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {ExportMetaByNode} meta - Served node definitions.
 * @return {TearsheetExportBody} Rows and series, ready to post.
 */
export function buildTearsheetExport(
  response: SlotValuationInputsResponse,
  meta: ExportMetaByNode
): TearsheetExportBody {
  return {
    rows: buildExportRows(response, meta),
    series: buildExportSeries(response, meta),
  };
}
